/**
 * Surface Layout Component
 * Phase 8: Surface Host Components - Layout Management with Peek Surfaces
 * 
 * Layout component that:
 * - Defines layout regions (sidebar, main, peek)
 * - Responsive breakpoints for mobile, tablet, desktop
 * - Grid/flex configuration for region arrangement
 * - Integrated peek surface support
 * - Mobile drawer logic with fc:sidebar:toggle event
 * - Props: sidebar, main, peek content
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Group, Stack, Container } from '@mantine/core';
import type { CSSProperties } from 'react';
import { usePeek } from './PeekManager';
import { eventBus } from '../../../core/event-bus';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';

/**
 * Breakpoint definitions (pixels)
 */
const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1400,
} as const;

/**
 * Layout configuration for different screen sizes
 */
interface LayoutConfig {
  sidebarWidth: number | string;
  peekWidth: number | string;
  mainFlexGrow: number;
  sidebarVisible: boolean;
  peekVisible: boolean;
  direction: 'column' | 'row';
  spacing: number | string;
  mobileDrawerOpen: boolean;
}

type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Layout configurations for each breakpoint
 */
const LAYOUT_CONFIGS: Record<BreakpointKey, LayoutConfig> = {
  mobile: {
    sidebarWidth: '100%',
    peekWidth: '100%',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: false,
    direction: 'column',
    spacing: 0,
    mobileDrawerOpen: false,
  },
  tablet: {
    sidebarWidth: '250px',
    peekWidth: '100%',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: false,
    direction: 'row',
    spacing: 0,
    mobileDrawerOpen: false,
  },
  desktop: {
    sidebarWidth: '250px',
    peekWidth: '300px',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: true,
    direction: 'row',
    spacing: 0,
    mobileDrawerOpen: false,
  },
  wide: {
    sidebarWidth: '280px',
    peekWidth: '320px',
    mainFlexGrow: 1,
    sidebarVisible: true,
    peekVisible: true,
    direction: 'row',
    spacing: 0,
    mobileDrawerOpen: false,
  },
};

interface SurfaceLayoutProps {
  /** Sidebar content */
  sidebar?: React.ReactNode;
  /** Main content area */
  main: React.ReactNode;
  /** Peek panel content (right panel) */
  peek?: React.ReactNode;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: CSSProperties;
  /** Enable responsive behavior (default: true) */
  responsive?: boolean;
  /** Force specific layout (overrides responsive) */
  forceLayout?: 'mobile' | 'tablet' | 'desktop' | 'wide';
  /** Custom spacing between regions */
  spacing?: number | string;
  /** Show debug borders */
  debugMode?: boolean;
  /** Mobile drawer open state (controlled) */
  mobileDrawerOpen?: boolean;
  /** Callback when mobile drawer should toggle */
  onMobileDrawerToggle?: () => void;
  /** Callback when mobile drawer closes */
  onMobileDrawerClose?: () => void;
}

/**
 * Surface Layout Component
 * 
 * Manages the layout of the surface application with sidebar, main content,
 * and peek panel. Automatically adjusts to screen size and supports custom configurations.
 * Includes mobile drawer logic with fc:sidebar:toggle event.
 */
const SurfaceLayout: React.FC<SurfaceLayoutProps> = ({
  sidebar,
  main,
  peek,
  className,
  style,
  responsive = true,
  forceLayout,
  spacing = 0,
  debugMode = false,
  mobileDrawerOpen = false,
  onMobileDrawerToggle,
  onMobileDrawerClose,
}) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<BreakpointKey>('desktop');
  const { sidebarCollapsed } = useWorkspace();

  // Source of truth for sidebar visibility is the workspace context.
  // The mobileDrawerOpen prop is kept for API compatibility but is no longer
  // consulted — the controlled-mode pattern was unused and collided with the
  // default prop value.
  const isDrawerOpen = !sidebarCollapsed;

  /**
   * Determine current layout based on screen size
   */
  const currentLayout = useMemo<BreakpointKey>(() => {
    // If forced layout is specified, use it
    if (forceLayout) {
      return forceLayout;
    }

    // If responsive is disabled, use desktop layout
    if (!responsive) {
      return 'desktop';
    }

    // Determine layout based on window width
    if (typeof window === 'undefined') {
      return 'desktop';
    }

    const width = window.innerWidth;

    if (width < BREAKPOINTS.mobile) {
      return 'mobile';
    } else if (width < BREAKPOINTS.tablet) {
      return 'tablet';
    } else if (width < BREAKPOINTS.desktop) {
      return 'desktop';
    }

    return 'wide';
  }, [forceLayout, responsive]);

  // Update current breakpoint on resize
  useEffect(() => {
    const handleResize = () => {
      // Update breakpoint on window resize
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < BREAKPOINTS.mobile) {
          setCurrentBreakpoint('mobile');
        } else if (width < BREAKPOINTS.tablet) {
          setCurrentBreakpoint('tablet');
        } else if (width < BREAKPOINTS.desktop) {
          setCurrentBreakpoint('desktop');
        } else {
          setCurrentBreakpoint('wide');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Get layout configuration for current breakpoint
   */
  const layoutConfig = useMemo(() => {
    return LAYOUT_CONFIGS[currentLayout];
  }, [currentLayout]);

  /**
   * Handle window resize to update layout
   */
  React.useEffect(() => {
    if (!responsive) return;

    const handleResize = () => {
      // Trigger re-render by updating state
      // This will cause layout recalculation
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [responsive]);

  /**
   * Close drawer when clicking overlay
   */
  const handleOverlayClick = () => {
    if (onMobileDrawerClose) {
      onMobileDrawerClose();
    }
    // No else: drawer is now controlled by the workspace context's
    // sidebarCollapsed, so the parent decides what to do.
  };

  /**
   * Render sidebar region
   */
  const renderSidebar = () => {
    if (!sidebar || !layoutConfig.sidebarVisible) {
      return null;
    }
    // When the workspace context says the sidebar is collapsed, hide it on
    // every breakpoint (desktop: collapse inline, mobile: skip the drawer).
    if (!isDrawerOpen) return null;

    const isMobile = currentLayout === 'mobile' || currentLayout === 'tablet';

    if (isMobile) {
      return (
        <>
          {/* Overlay backdrop */}
          <Box
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
            onClick={handleOverlayClick}
          />
          {/* Drawer */}
          <Box
            component="nav"
            w="280px"
            h="100%"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              backgroundColor: 'var(--mantine-color-white)',
              borderRight: '1px solid var(--mantine-color-gray-2)',
              overflowY: 'auto',
              overflowX: 'hidden',
              zIndex: 1000,
              boxShadow: '4px 0 12px rgba(0, 0, 0, 0.15)',
              animation: 'slideInLeft 200ms ease-out',
              ...(debugMode && {
                outline: '2px solid red',
              }),
            }}
          >
            {sidebar}
          </Box>
        </>
      );
    }

    // Desktop: render inline
    return (
      <Box
        component="nav"
        w={layoutConfig.sidebarWidth}
        h="100%"
        style={{
          flexShrink: 0,
          borderRight: '1px solid var(--mantine-color-gray-2)',
          overflowY: 'auto',
          overflowX: 'hidden',
          ...(debugMode && {
            outline: '2px solid red',
          }),
        }}
      >
        {sidebar}
      </Box>
    );
  };

  /**
   * Render main content region
   */
  const renderMain = () => {
    return (
      <Box
        component="main"
        flex={layoutConfig.mainFlexGrow}
        h="100%"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(debugMode && {
            outline: '2px solid green',
          }),
        }}
      >
        {main}
      </Box>
    );
  };

  /**
   * Render peek panel region
   */
  const renderPeek = () => {
    if (!peek || !layoutConfig.peekVisible) {
      return null;
    }

    return (
      <Box
        component="aside"
        w={layoutConfig.peekWidth}
        h="100%"
        style={{
          flexShrink: 0,
          borderLeft: '1px solid var(--mantine-color-gray-2)',
          overflowY: 'auto',
          overflowX: 'hidden',
          ...(debugMode && {
            outline: '2px solid blue',
          }),
        }}
      >
        {peek}
      </Box>
    );
  };

  return (
    <Box
      className={className}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: layoutConfig.direction as any,
        gap: layoutConfig.spacing,
        position: 'relative',
        ...style,
      }}
    >
      {/* Sidebar */}
      {renderSidebar()}

      {/* Main content */}
      {renderMain()}

      {/* Peek panel */}
      {renderPeek()}

      {/* Debug info */}
      {debugMode && (
        <Box
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            fontSize: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            zIndex: 9999,
          }}
        >
          <div>Layout: {currentLayout}</div>
          <div>Width: {typeof window !== 'undefined' ? window.innerWidth : 'N/A'}px</div>
          <div>Sidebar: {layoutConfig.sidebarVisible ? 'visible' : 'hidden'}</div>
          <div>Peek: {layoutConfig.peekVisible ? 'visible' : 'hidden'}</div>
          <div>Drawer: {isDrawerOpen ? 'open' : 'closed'}</div>
        </Box>
      )}

      {/* Slide-in animation styles */}
      <style>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </Box>
  );
};

export default SurfaceLayout;
export type { SurfaceLayoutProps };
export { BREAKPOINTS, LAYOUT_CONFIGS, type LayoutConfig };